import { ReactNode, useRef, useState } from "react";
import { Button, Flex, FormInstance, Input, InputNumber, ModalProps, Typography } from "antd";
import { useTranslation } from "react-i18next";
import SModalForm from "@/styles/SModalForm";
import { Supplier, SupplierAdd } from "@/types/purchase";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect, } from "@/components/Selector";

interface SupplierFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: SupplierAdd) => Promise<void>;
  data: Supplier | null;
  leftTitle: ReactNode;
  confirmLoading: boolean;
}

const SupplierForm = (props: SupplierFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance<SupplierAdd>>(null);
  const [showExtFields, setShowExtFields] = useState<boolean>(false);


  const getInput = (props: { maxLength: number; placeholder: string }) => {
    return (
      <Input
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        showCount
      />
    );
  };

  const getPercent = (props: { placeholder: string }) => {
    return (
      <InputNumber changeOnWheel
        max={100}
        min={0}
        style={{ width: "100%" }}
        placeholder={props.placeholder}
        formatter={(value) => `${value ? value + "%" : ""}`}
      />
    );
  };

  const getInputNumber = (props: { placeholder: string }) => {
    return (
      <InputNumber changeOnWheel
        precision={0}
        min={0}
        style={{ width: "100%" }}
        placeholder={props.placeholder}
      />
    );
  };

  return (
    <SModalForm<SupplierAdd>
      width={600}
      modelFormRef={formRef}
      formItems={[
        {
          label: t("purchase.code"),
          name: "code",
          children: !props.data ? (
            <Input
              placeholder={t("purchase.plCode")}
              maxLength={10}
              showCount
            />
          ) : (
            <Typography.Title level={4} style={{ margin: 0 }}>
              {props.data.code}
            </Typography.Title>
          ),
          required: true,
          rules: [{
            required: true, message: t("purchase.plCode"), validator: (rule, value, callback) => {
              if (!value || (value && value.trim() === '')) {
                return Promise.reject();
              }
              return Promise.resolve();
            },
          }],
        },
        {
          label: t("purchase.principal"),
          name: "principal",
          children: getInput({
            placeholder: t("purchase.plPrincipal"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.name"),
          name: "name",
          children: getInput({
            placeholder: t("purchase.plName"),
            maxLength: 100,
          }),
        },
        {
          label: t("purchase.shortName"),
          name: "shortName",
          children: getInput({
            placeholder: t("purchase.plShortName"),
            maxLength: 20,
          }),
        },
        {
          label: t("purchase.country"),
          name: "country",
          children: <CountrySelect />,
        },
        {
          label: t("purchase.address"),
          name: "address",
          children:
            <Input.TextArea
              placeholder={t("purchase.plAddress")}
              maxLength={500}
              showCount
              autoSize={{ maxRows: 4, minRows: 2 }}
            />
        },
        {
          label: t("purchase.zipCode"),
          name: "zipCode",
          children: getInput({
            placeholder: t("purchase.plZipCode"),
            maxLength: 10,
          }),
        },
        {
          label: t("purchase.email"),
          name: "email",
          children: getInput({ placeholder: t("purchase.plEmail"), maxLength: 100 }),
        },
        {
          label: t("purchase.contact"),
          name: "contact",
          children: getInput({
            placeholder: t("purchase.plContact"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.phone"),
          name: "phone",
          children: getInput({ placeholder: t("purchase.plPhone"), maxLength: 50 }),
        },
        {
          label: t("purchase.fax"),
          name: "fax",
          children: getInput({ placeholder: t("purchase.plFax"), maxLength: 50 }),
        },
        {
          label: t("purchase.mobile"),
          name: "mobile",
          children: getInput({
            placeholder: t("purchase.plMobile"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.account1"),
          name: "account1",
          children: getInput({
            placeholder: t("purchase.plAccount1"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.bank1"),
          name: "bank1",
          children: getInput({
            placeholder: t("purchase.plBank1"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.swiftCode1"),
          name: "swiftCode1",
          children: getInput({
            placeholder: t("purchase.plSwiftCode1"),
            maxLength: 20,
          }),
        },
        {
          label: t("purchase.account2"),
          name: "account2",
          children: getInput({
            placeholder: t("purchase.plAccount2"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.bank2"),
          name: "bank2",
          children: getInput({
            placeholder: t("purchase.plBank2"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.swiftCode2"),
          name: "swiftCode2",
          children: getInput({
            placeholder: t("purchase.plSwiftCode2"),
            maxLength: 20,
          }),
        },
        {
          label: t("purchase.currency"),
          name: "currency",
          children: <CurrencySelect />,
        },
        {
          label: t("purchase.creditLevel"),
          name: "creditLevel",
          children: <CreditLevelSelect />,
        },
        {
          label: t("purchase.creditLine"),
          name: "creditLine",
          children: getInputNumber({ placeholder: t("purchase.plCreditLine") }),
        },
        {
          label: t("purchase.depositRate"),
          name: "depositRate",
          children: getPercent({ placeholder: t("purchase.plDepositRate") }),
        },
        {
          label: t("purchase.tradeTerm"),
          name: "tradeTerm",
          children: <Dict key={"tradeTerm"} type={DictEnum.PURCHASE_TRADE_TERM} />,
        },
        {
          label: t("purchase.payMethod"),
          name: "payMethod",
          children: <Dict key={"payMethod"} type={DictEnum.PURCHASE_PAY_METHOD} />,
        },
        {
          label: t("purchase.org"),
          name: "orgId",
          children: <DepartmentSelect placeholder={t("purchase.org")} />,
        },
        {
          label: t("purchase.billingDays"),
          name: "billingDays",
          children: getInputNumber({ placeholder: t("purchase.plBillingDays") }),
        },
        {
          label: t("purchase.affiliates"),
          name: "affiliates",
          children: getInput({
            placeholder: t("purchase.plAffiliates"),
            maxLength: 50,
          }),
        },
        {
          label: t("purchase.taxRate"),
          name: "taxRate",
          children: getPercent({ placeholder: t("purchase.plTaxRate") }),
        },
        {
          label: t("purchase.thdCode"),
          name: "thdCode",
          children: getInput({
            placeholder: t("purchase.plThdCode"),
            maxLength: 200,
          }),
        },
        {
          noStyle: true,
          children: (
            <Flex justify="end" style={{ marginTop: -18 }}>
              <Button type="link" onClick={() => setShowExtFields(!showExtFields)}>
                {showExtFields ? t('common.collapse') : t('common.expand')}
              </Button>
            </Flex>
          )
        },
        {
          label: t("purchase.wmsApi"),
          name: "wmsApi",
          hidden: !showExtFields,
          children: getInput({
            placeholder: t("purchase.plWmsApi"),
            maxLength: 200,
          }),
        },
      ]}
      {...props}
    />
  );
};

export default SupplierForm;
